import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Card,
  CardContent,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  getDashboardStatistics,
  getInventoryItems,
  getInventoryItemDetails,
  getAllInventory,
  getLowStockItems,
  getOutOfStockItems,
  adjustInventory,
  getProductTransactionHistory,
  getInventoryAlerts,
  acknowledgeAlert,
  reverseTransaction,
  getInventoryStatistics,
  getLowStockReport,
  getOverstockReport,
  getOutOfStockReport,
  getBestSellers,
} from '../services/inventoryService';

const InventoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State Management
  const [inventory, setInventory] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1); // 1-based for backend
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0: all, 1: lowstock, 2: outofstock, 3: bestsellers
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  // Dialog States
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPageSize, setTransactionPageSize] = useState(20);
  const [transactionTotalItems, setTransactionTotalItems] = useState(0);
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: 0,
    reason: '',
    type: 'increase', // 'increase' or 'decrease'
  });

  useEffect(() => {
    fetchDashboardData();
    fetchAlerts();
  }, []);

  useEffect(() => {
    fetchInventoryData();
  }, [searchQuery, activeTab, page, rowsPerPage, sortBy, sortOrder, dateRange]);

  const fetchDashboardData = async () => {
    try {
      const response = await getDashboardStatistics();
      const statsData = response?.payload || response?.data || response || {};
      // Map backend field names to our state
      const mappedStats = {
        totalProducts: statsData.TotalProducts || 0,
        inStockProducts: statsData.InStockProducts || 0,
        lowStockItems: statsData.LowStockProducts || 0,
        outOfStock: statsData.OutOfStockProducts || 0,
        totalStockValue: statsData.TotalInventoryValue || 0,
        totalInventoryQuantity: statsData.TotalInventoryQuantity || 0,
        activeAlerts: statsData.ActiveAlerts || 0,
        criticalAlerts: statsData.CriticalAlerts || 0,
        unacknowledgedAlerts: statsData.UnacknowledgedAlerts || 0,
        totalTransactionsToday: statsData.TotalTransactionsToday || 0,
        purchaseTransactionsToday: statsData.PurchaseTransactionsToday || 0,
        saleTransactionsToday: statsData.SaleTransactionsToday || 0,
        totalPurchasesValue: statsData.TotalPurchasesValue || 0,
        totalSalesValue: statsData.TotalSalesValue || 0,
      };
      setDashboardStats(mappedStats);
      console.log('✅ Dashboard statistics loaded:', mappedStats);
    } catch (error) {
      console.error('❌ Error fetching dashboard statistics:', error);
      toast.error('Failed to load dashboard statistics');
    }
  };

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      
      // Tab 0: All Items, Tab 1: Low Stock Report, Tab 2: Overstock Report, Tab 3: Best Sellers
      if (activeTab === 1) {
        // Low Stock Report
        const response = await getLowStockReport(page, rowsPerPage);
        const data = response?.payload || response?.data || response || {};
        
        console.log('✅ Low stock report loaded:', data);
        
        if (data.Items) {
          // Map low stock report to inventory format
          const mappedItems = data.Items.map(item => ({
            ProductId: item.ProductId,
            ProductName: item.ProductName,
            SKU: item.ProductSKU,
            CurrentStock: item.CurrentStock,
            MinStock: item.MinStockLevel,
            MaxStock: null,
            StockLevel: 'LowStock',
            Status: item.Priority,
            LastUpdated: null,
            CostPrice: item.CostPrice,
            SalePrice: 0,
            InventoryValue: item.CurrentStock * item.CostPrice,
            StockDeficit: item.StockDeficit,
            SuggestedOrderQuantity: item.SuggestedOrderQuantity,
            EstimatedOrderCost: item.EstimatedOrderCost,
            SupplierName: item.SupplierName,
          }));
          setInventory(mappedItems);
          setTotalItems(data.TotalItems || 0);
        } else {
          setInventory([]);
          setTotalItems(0);
        }
      } else if (activeTab === 2) {
        // Overstock Report (for Out of Stock tab)
        const response = await getOverstockReport(page, rowsPerPage);
        const data = response?.payload || response?.data || response || {};
        
        console.log('✅ Overstock report loaded:', data);
        
        if (data.Items) {
          // Map overstock report to inventory format
          const mappedItems = data.Items.map(item => ({
            ProductId: item.ProductId,
            ProductName: item.ProductName,
            SKU: item.ProductSKU,
            CurrentStock: item.CurrentStock,
            MinStock: item.MinStockLevel,
            MaxStock: null,
            StockLevel: 'Overstock',
            Status: 'Overstock',
            LastUpdated: null,
            CostPrice: 0,
            SalePrice: 0,
            InventoryValue: item.InventoryValue,
            ExcessStock: item.ExcessStock,
            DaysOfStockOnHand: item.DaysOfStockOnHand,
            TiedUpCapital: item.TiedUpCapital,
            LastSaleDate: item.LastSaleDate,
            DaysSinceLastSale: item.DaysSinceLastSale,
            AverageDailySales: item.AverageDailySales,
          }));
          setInventory(mappedItems);
          setTotalItems(data.TotalItems || 0);
        } else {
          setInventory([]);
          setTotalItems(0);
        }
      } else if (activeTab === 3) {
        // Best Sellers Report
        const response = await getBestSellers(
          dateRange.startDate ? new Date(dateRange.startDate).toISOString() : undefined,
          dateRange.endDate ? new Date(dateRange.endDate).toISOString() : undefined,
          page,
          rowsPerPage
        );
        const data = response?.payload || response?.data || response || {};
        
        console.log('✅ Best sellers loaded:', data);
        
        if (data.Items) {
          // Map best sellers to inventory format
          const mappedItems = data.Items.map(item => ({
            ProductId: item.ProductId,
            ProductName: item.ProductName,
            SKU: item.ProductSKU,
            CurrentStock: item.CurrentStock,
            MinStock: 0,
            MaxStock: null,
            StockLevel: 'BestSeller',
            Status: 'Best Seller',
            LastUpdated: null,
            CostPrice: item.AverageCostPrice,
            SalePrice: item.AverageSalePrice,
            InventoryValue: 0,
            TotalQuantitySold: item.TotalQuantitySold,
            TotalRevenue: item.TotalRevenue,
            TotalProfit: item.TotalProfit,
            TransactionCount: item.TransactionCount,
            AverageSalePrice: item.AverageSalePrice,
          }));
          setInventory(mappedItems);
          setTotalItems(data.TotalItems || 0);
        } else {
          setInventory([]);
          setTotalItems(0);
        }
      } else if (activeTab === 4) {
        // Out of Stock Report
        const response = await getOutOfStockReport(page, rowsPerPage);
        const data = response?.payload || response?.data || response || {};
        
        console.log('✅ Out of stock report loaded:', data);
        
        if (data.Items) {
          // Map out of stock report to inventory format
          const mappedItems = data.Items.map(item => ({
            ProductId: item.ProductId,
            ProductName: item.ProductName,
            SKU: item.ProductSKU,
            BarCode: item.BarCode,
            CategoryName: item.CategoryName,
            BrandName: item.BrandName,
            CurrentStock: 0,
            MinStock: item.MinStockLevel,
            MaxStock: null,
            StockLevel: 'OutOfStock',
            Status: item.Priority || 'Critical',
            CostPrice: item.CostPrice,
            SalePrice: item.SalePrice,
            ReorderQuantity: item.ReorderQuantity,
            EstimatedOrderCost: item.EstimatedOrderCost,
            SupplierName: item.SupplierName,
            LastRestockDate: item.LastRestockDate,
            LastSaleDate: item.LastSaleDate,
            DaysOutOfStock: item.DaysOutOfStock,
            LostSalesEstimate: item.LostSalesEstimate,
          }));
          setInventory(mappedItems);
          setTotalItems(data.TotalItems || 0);
        } else {
          setInventory([]);
          setTotalItems(0);
        }
      } else {
        // All Items - use standard API
        const params = {
          searchTerm: searchQuery.trim() || undefined,
          filter: 'all',
          page: page,
          pageSize: rowsPerPage,
          sortBy: sortBy,
          sortOrder: sortOrder,
        };
        
        console.log('📦 Fetching inventory with params:', params);
        const response = await getInventoryItems(params);
        const data = response?.payload || response?.data || response || {};
        
        console.log('✅ Inventory data loaded:', data);
        
        // Extract dashboard stats from response (if available)
        if (data.DashboardStats) {
          const dashStats = data.DashboardStats;
          const mappedStats = {
            totalProducts: dashStats.TotalProducts || 0,
            lowStockItems: dashStats.LowStockItems || 0,
            outOfStock: dashStats.OutOfStock || 0,
            totalStockValue: dashStats.TotalStockValue || 0,
          };
          setDashboardStats(prevStats => ({ ...prevStats, ...mappedStats }));
        }
        
        // Extract items from nested structure
        if (data.Items) {
          const items = data.Items.Items || [];
          const totalCount = data.Items.TotalItems || 0;
          setInventory(items);
          setTotalItems(totalCount);
          console.log('✅ Loaded', items.length, 'items out of', totalCount);
        } else {
          setInventory([]);
          setTotalItems(0);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching inventory data:', error);
      toast.error('Failed to load inventory data');
      setInventory([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await getInventoryAlerts();
      const data = response?.payload || response?.data || response || {};
      // Handle paginated alerts response
      const alertsData = data.Items || [];
      setAlerts(alertsData.filter(alert => !alert.IsAcknowledged));
      console.log('✅ Alerts loaded:', alertsData.length);
    } catch (error) {
      console.error('❌ Error fetching alerts:', error);
    }
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchDashboardData(),
        fetchInventoryData(),
        fetchAlerts(),
      ]);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustInventory = (product) => {
    setSelectedProduct(product);
    setAdjustmentData({
      quantity: 0,
      reason: '',
      type: 'increase',
    });
    setAdjustDialogOpen(true);
  };

  const handleSaveAdjustment = async () => {
    try {
      if (!adjustmentData.quantity || adjustmentData.quantity <= 0) {
        toast.error('Please enter a valid quantity');
        return;
      }

      if (!adjustmentData.reason.trim()) {
        toast.error('Please provide a reason for adjustment');
        return;
      }

      // Match backend InventoryAdjustmentDto
      const adjustment = {
        ProductId: selectedProduct.ProductId,
        QuantityChange: adjustmentData.type === 'increase' 
          ? Math.abs(adjustmentData.quantity) 
          : -Math.abs(adjustmentData.quantity),
        Reason: adjustmentData.reason,
        ReferenceNumber: null, // Optional
      };

      await adjustInventory(adjustment);
      toast.success('Inventory adjusted successfully');
      setAdjustDialogOpen(false);
      fetchInventoryData();
      fetchDashboardData();
    } catch (error) {
      console.error('❌ Error adjusting inventory:', error);
      toast.error(error.response?.data?.message || 'Failed to adjust inventory');
    }
  };

  const handleViewHistory = async (product, page = 1) => {
    try {
      setSelectedProduct(product);
      const response = await getProductTransactionHistory(product.ProductId, page, transactionPageSize);
      const data = response?.payload || response?.data || response || {};
      
      // Handle paginated response
      if (data.Items) {
        setTransactionHistory(data.Items || []);
        setTransactionTotalItems(data.TotalItems || 0);
        setTransactionPage(page);
      } else {
        // Fallback for non-paginated response
        setTransactionHistory(data || []);
        setTransactionTotalItems(data.length || 0);
      }
      
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('❌ Error fetching transaction history:', error);
      toast.error('Failed to load transaction history');
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await acknowledgeAlert(alertId);
      toast.success('Alert acknowledged');
      fetchAlerts();
    } catch (error) {
      console.error('❌ Error acknowledging alert:', error);
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleReverseTransaction = async (transactionId) => {
    if (window.confirm('Are you sure you want to reverse this transaction?')) {
      try {
        await reverseTransaction(transactionId);
        toast.success('Transaction reversed successfully');
        handleViewHistory(selectedProduct);
        fetchInventoryData();
        fetchDashboardData();
      } catch (error) {
        console.error('❌ Error reversing transaction:', error);
        toast.error(error.response?.data?.message || 'Failed to reverse transaction');
      }
    }
  };

  const getStockStatusColor = (item) => {
    const status = item.Status || item.StockLevel;
    if (status === 'Out' || status === 'OutOfStock' || item.CurrentStock === 0) return 'error';
    if (status === 'Low' || status === 'Critical' || status === 'LowStock' || item.CurrentStock <= item.MinStock) return 'warning';
    return 'success';
  };

  const getStockStatusText = (item) => {
    const status = item.Status || item.StockLevel;
    if (status === 'Out' || status === 'OutOfStock' || item.CurrentStock === 0) return 'Out of Stock';
    if (status === 'Low' || status === 'LowStock') return 'Low Stock';
    if (status === 'Critical') return 'Critical';
    return 'In Stock';
  };

  const calculateStockPercentage = (item) => {
    if (!item.MaxStock || item.MaxStock === 0) return 100;
    return (item.CurrentStock / item.MaxStock) * 100;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InventoryIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('inventory_management') || 'Inventory Management'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('track_and_manage_stock') || 'Track and manage your product stock levels'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchAllData}
          sx={{ borderRadius: 2 }}
        >
          {t('refresh') || 'Refresh'}
        </Button>
      </Box>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => alerts.forEach(alert => handleAcknowledgeAlert(alert.Id))}
            >
              Acknowledge All
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {alerts.length} Active Alert{alerts.length > 1 ? 's' : ''}
          </Typography>
          {alerts.slice(0, 3).map(alert => (
            <Typography key={alert.Id} variant="caption" display="block">
              • [{alert.Severity}] {alert.Message}
            </Typography>
          ))}
          {alerts.length > 3 && (
            <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
              ... and {alerts.length - 3} more
            </Typography>
          )}
        </Alert>
      )}

      {/* Statistics Cards - Row 1 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd', borderLeft: '4px solid', borderColor: 'primary.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Products
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {dashboardStats?.totalProducts || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    In Stock: {dashboardStats?.inStockProducts || 0}
                  </Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Low Stock Items
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {dashboardStats?.lowStockItems || 0}
                  </Typography>
                </Box>
                <TrendingDownIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#ffebee', borderLeft: '4px solid', borderColor: 'error.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Out of Stock
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>
                    {dashboardStats?.outOfStock || 0}
                  </Typography>
                </Box>
                <ErrorIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Stock Value
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    €{dashboardStats?.totalStockValue?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Qty: {dashboardStats?.totalInventoryQuantity?.toLocaleString() || 0}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Statistics Cards - Row 2: Transactions Today */}
      {dashboardStats?.totalTransactionsToday > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#f3e5f5', borderLeft: '4px solid', borderColor: 'secondary.main' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Transactions Today
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {dashboardStats?.totalTransactionsToday || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fff9e6' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Purchases Today
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {dashboardStats?.purchaseTransactionsToday || 0} (€{dashboardStats?.totalPurchasesValue?.toFixed(2) || '0.00'})
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e8f5e9' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Sales Today
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {dashboardStats?.saleTransactionsToday || 0} (€{dashboardStats?.totalSalesValue?.toFixed(2) || '0.00'})
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#ffebee' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Active Alerts
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {dashboardStats?.activeAlerts || 0} ({dashboardStats?.criticalAlerts || 0} critical)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            setPage(1); // Reset to first page when changing tabs
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={
              <Badge badgeContent={dashboardStats?.totalProducts || 0} color="primary">
                All Items
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={dashboardStats?.lowStockItems || 0} color="warning">
                Low Stock
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge color="error">
                Overstock
              </Badge>
            } 
          />
          <Tab 
            label="Best Sellers"
          />
          <Tab 
            label={
              <Badge badgeContent={dashboardStats?.outOfStock || 0} color="error">
                Out of Stock
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Search and Sort Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={activeTab === 3 ? 3 : 6}>
            <TextField
              fullWidth
              placeholder="Search by product name or SKU..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1); // Reset to first page when searching
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {activeTab === 3 && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={dateRange.startDate}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, startDate: e.target.value });
                    setPage(1);
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={dateRange.endDate}
                  onChange={(e) => {
                    setDateRange({ ...dateRange, endDate: e.target.value });
                    setPage(1);
                  }}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={6} md={activeTab === 3 ? 2.5 : 3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name">Product Name</MenuItem>
                <MenuItem value="sku">SKU</MenuItem>
                <MenuItem value="currentstock">Current Stock</MenuItem>
                <MenuItem value="inventoryvalue">Inventory Value</MenuItem>
                <MenuItem value="lastupdated">Last Updated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={activeTab === 3 ? 2.5 : 3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Inventory Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Current Stock</TableCell>
                {activeTab !== 3 && activeTab !== 4 && <TableCell sx={{ fontWeight: 700 }} align="center">Min Stock</TableCell>}
                {activeTab === 0 && <TableCell sx={{ fontWeight: 700 }} align="center">Max Stock</TableCell>}
                {activeTab === 1 && <TableCell sx={{ fontWeight: 700 }} align="center">Stock Deficit</TableCell>}
                {activeTab === 2 && <TableCell sx={{ fontWeight: 700 }} align="center">Excess Stock</TableCell>}
                {activeTab === 4 && <TableCell sx={{ fontWeight: 700 }} align="center">Reorder Qty</TableCell>}
                {activeTab !== 2 && activeTab !== 3 && activeTab !== 4 && <TableCell sx={{ fontWeight: 700 }}>Stock Level</TableCell>}
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                {activeTab === 1 && <TableCell sx={{ fontWeight: 700 }} align="center">Suggested Order</TableCell>}
                {activeTab === 2 && <TableCell sx={{ fontWeight: 700 }} align="center">Days on Hand</TableCell>}
                {activeTab === 3 && <TableCell sx={{ fontWeight: 700 }} align="center">Qty Sold</TableCell>}
                {activeTab === 4 && <TableCell sx={{ fontWeight: 700 }} align="center">Days Out</TableCell>}
                {activeTab !== 2 && activeTab !== 4 && <TableCell sx={{ fontWeight: 700 }} align="right">Cost Price</TableCell>}
                {activeTab === 0 && <TableCell sx={{ fontWeight: 700 }} align="right">Sale Price</TableCell>}
                {activeTab === 1 && <TableCell sx={{ fontWeight: 700 }} align="right">Est. Order Cost</TableCell>}
                {activeTab === 2 && <TableCell sx={{ fontWeight: 700 }} align="right">Tied Up Capital</TableCell>}
                {activeTab === 3 && <TableCell sx={{ fontWeight: 700 }} align="right">Revenue</TableCell>}
                {activeTab === 3 && <TableCell sx={{ fontWeight: 700 }} align="right">Profit</TableCell>}
                {activeTab === 3 && <TableCell sx={{ fontWeight: 700 }} align="center">Transactions</TableCell>}
                {activeTab === 4 && <TableCell sx={{ fontWeight: 700 }} align="right">Est. Order Cost</TableCell>}
                {activeTab === 4 && <TableCell sx={{ fontWeight: 700 }} align="right">Lost Sales</TableCell>}
                {activeTab === 0 && <TableCell sx={{ fontWeight: 700 }} align="right">Inventory Value</TableCell>}
                {activeTab === 1 && <TableCell sx={{ fontWeight: 700 }}>Supplier</TableCell>}
                {activeTab === 2 && <TableCell sx={{ fontWeight: 700 }}>Last Sale</TableCell>}
                {activeTab === 4 && <TableCell sx={{ fontWeight: 700 }}>Last Restock</TableCell>}
                {activeTab === 0 && <TableCell sx={{ fontWeight: 700 }}>Last Updated</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeTab === 1 ? 13 : 12} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No inventory items found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inventory.map((item) => (
                  <TableRow key={item.Id || item.ProductId} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.ProductName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.SKU || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.CurrentStock}
                          size="small"
                          color={getStockStatusColor(item)}
                          sx={{ fontWeight: 600, minWidth: 60 }}
                        />
                      </TableCell>
                      {activeTab !== 3 && activeTab !== 4 && (
                        <TableCell align="center">
                          <Typography variant="body2">{item.MinStock}</Typography>
                        </TableCell>
                      )}
                      {activeTab === 0 && (
                        <TableCell align="center">
                          <Typography variant="body2">{item.MaxStock || '-'}</Typography>
                        </TableCell>
                      )}
                      {activeTab === 1 && (
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                            {item.StockDeficit?.toFixed(2) || 0}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 2 && (
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>
                            {item.ExcessStock?.toFixed(2) || 0}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 4 && (
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {item.ReorderQuantity || 0}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab !== 2 && activeTab !== 3 && activeTab !== 4 && (
                        <TableCell>
                          <Box sx={{ width: '100%', minWidth: 100 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(calculateStockPercentage(item), 100)}
                              color={getStockStatusColor(item)}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(calculateStockPercentage(item))}%
                            </Typography>
                          </Box>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={getStockStatusText(item)}
                          size="small"
                          color={getStockStatusColor(item)}
                          icon={
                            item.CurrentStock === 0 ? <ErrorIcon /> :
                            item.CurrentStock <= item.MinStock ? <WarningIcon /> :
                            <CheckCircleIcon />
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      {activeTab === 1 && (
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.SuggestedOrderQuantity || '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 2 && (
                        <TableCell align="center">
                          <Typography variant="body2">
                            {item.DaysOfStockOnHand || 0}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 3 && (
                        <TableCell align="center">
                          <Chip
                            label={item.TotalQuantitySold || 0}
                            size="small"
                            color="success"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      )}
                      {activeTab === 4 && (
                        <TableCell align="center">
                          <Chip
                            label={item.DaysOutOfStock || 0}
                            size="small"
                            color="error"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                      )}
                      {activeTab !== 2 && activeTab !== 4 && (
                        <TableCell align="right">
                          <Typography variant="body2">
                            €{item.CostPrice?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 0 && (
                        <TableCell align="right">
                          <Typography variant="body2">
                            €{item.SalePrice?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 1 && (
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            €{item.EstimatedOrderCost?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 2 && (
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                            €{item.TiedUpCapital?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 3 && (
                        <>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              €{item.TotalRevenue?.toFixed(2) || '0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'info.main' }}>
                              €{item.TotalProfit?.toFixed(2) || '0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.TransactionCount || 0}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                        </>
                      )}
                      {activeTab === 4 && (
                        <>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                              €{item.EstimatedOrderCost?.toFixed(2) || '0.00'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                              €{item.LostSalesEstimate?.toFixed(2) || '0.00'}
                            </Typography>
                          </TableCell>
                        </>
                      )}
                      {activeTab === 0 && (
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            €{item.InventoryValue?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 1 && (
                        <TableCell>
                          <Typography variant="caption">
                            {item.SupplierName || '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 2 && (
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(item.LastSaleDate)}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 4 && (
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(item.LastRestockDate)}
                          </Typography>
                        </TableCell>
                      )}
                      {activeTab === 0 && (
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(item.LastUpdated)}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/inventory/${item.ProductId || item.Id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Adjust Stock">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleAdjustInventory(item)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View History">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewHistory(item)}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page - 1} // MUI uses 0-based index
          onPageChange={(e, newPage) => setPage(newPage + 1)} // Convert back to 1-based
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(1); // Reset to first page
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Adjust Inventory Dialog */}
      <Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Adjust Inventory - {selectedProduct?.ProductName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Adjustment Type</InputLabel>
              <Select
                value={adjustmentData.type}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, type: e.target.value })}
                label="Adjustment Type"
              >
                <MenuItem value="increase">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon color="success" />
                    Increase Stock
                  </Box>
                </MenuItem>
                <MenuItem value="decrease">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RemoveIcon color="error" />
                    Decrease Stock
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Quantity"
              value={adjustmentData.quantity}
              onChange={(e) => setAdjustmentData({ ...adjustmentData, quantity: parseInt(e.target.value) || 0 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {adjustmentData.type === 'increase' ? <AddIcon color="success" /> : <RemoveIcon color="error" />}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Adjustment"
              value={adjustmentData.reason}
              onChange={(e) => setAdjustmentData({ ...adjustmentData, reason: e.target.value })}
              placeholder="e.g., Damaged goods, Stock count correction, etc."
            />

            <Alert severity="info">
              Current Stock: <strong>{selectedProduct?.CurrentStock}</strong> units
              <br />
              New Stock: <strong>
                {adjustmentData.type === 'increase'
                  ? (selectedProduct?.CurrentStock || 0) + (adjustmentData.quantity || 0)
                  : (selectedProduct?.CurrentStock || 0) - (adjustmentData.quantity || 0)}
              </strong> units
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAdjustment} variant="contained" color="primary">
            Save Adjustment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Transaction History - {selectedProduct?.ProductName}
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Document</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Qty Change</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Previous</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">New Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Unit Cost</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total Value</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactionHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No transaction history available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactionHistory.map((transaction) => (
                    <TableRow key={transaction.Id} sx={{ bgcolor: transaction.IsReversed ? '#ffebee' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(transaction.TransactionDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {transaction.TransactionReference || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.TransactionType}
                          size="small"
                          color={
                            transaction.TransactionType?.includes('Sale') || 
                            transaction.TransactionType?.includes('Decrease') ? 'error' : 'success'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {transaction.SourceDocumentNumber || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: transaction.QuantityChange > 0 ? 'success.main' : 'error.main'
                          }}
                        >
                          {transaction.QuantityChange > 0 ? '+' : ''}{transaction.QuantityChange}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{transaction.PreviousQuantity}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {transaction.NewQuantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">
                          {transaction.UnitCost ? `€${transaction.UnitCost.toFixed(2)}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {transaction.TotalValue ? `€${transaction.TotalValue.toFixed(2)}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{transaction.Notes || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {transaction.IsReversed ? (
                          <Chip label="Reversed" size="small" color="error" />
                        ) : (
                          <Chip label="Active" size="small" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {transactionTotalItems > transactionPageSize && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <TablePagination
                component="div"
                count={transactionTotalItems}
                page={transactionPage - 1}
                onPageChange={(e, newPage) => handleViewHistory(selectedProduct, newPage + 1)}
                rowsPerPage={transactionPageSize}
                onRowsPerPageChange={(e) => {
                  setTransactionPageSize(parseInt(e.target.value, 10));
                  handleViewHistory(selectedProduct, 1);
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InventoryPage;